import isEqual from "lodash/isEqual";
import cloneDeep from "lodash/cloneDeep";

import { putRequestCase, postRequestCase, deleteRequestCase } from "@/stores/caseStore/backendApi";
import { useCaseStore } from "@/stores/caseStore/0_caseStore";
import { withQuestionnaireSource } from "@/stores/caseStore/questionnaireSource";
import { useSnackbarStore } from "@/stores/snackbarStore";
import * as d from "@/utils/dataUtils";

// model

export const defaultWitness = {
  witnessType: null,
  roles: null,
  relation: null,
  injured: null,
  salutation: null,
  title: null,
  firstName: "",
  lastName: "",

  contactDataAvailable: null,
  streetNo: "",
  zip: "",
  city: "",
  country: null,
  mobile: "",
  landline: "",
  email: "",

  remarks: "",
  remarksDe: null,
  originalLanguage: null,
};

// CRUD

export async function saveWitnessesMaster(): Promise<void> {
  const store = useCaseStore();
  const snackbar = useSnackbarStore();

  try {
    const current = store.caseData.hasWitnesses;
    const previous = store.lastSavedCaseData.hasWitnesses;

    if (isEqual(current, previous)) return;

    await putRequestCase(withQuestionnaireSource(`/protected/cases/${store.caseData.id}/questionnaire`, store.questionnaireView), { hasWitnesses: current });
    store.lastSavedCaseData.hasWitnesses = current;
  } catch (error: unknown) {
    const msg = error instanceof Error ? error.message : (error as any)?.message || "Fehler beim Speichern";
    snackbar.showSnackbar({ message: msg, type: "error" });
    // Wie saveAccident/savePoliceProsecution weiterreichen, damit Aufrufer
    // (z. B. das Cockpit-Fenster) keinen Erfolg melden, wenn nichts gespeichert wurde.
    throw error;
  }
}

export async function saveWitnesses(): Promise<void> {
  const store = useCaseStore();
  const snackbar = useSnackbarStore();

  try {
    const current = store.witnesses.map(prepareWitnessForSave);
    const previous = store.lastSavedWitnesses.map(prepareWitnessForSave);

    const hasChanged = !isEqual(current, previous);

    if (!hasChanged) return;

    for (const witness of current) {
      await putRequestCase(withQuestionnaireSource(`/protected/cases/${store.caseData.id}/witnesses/${witness.id}`, store.questionnaireView), witness);
    }

    store.lastSavedWitnesses = cloneDeep(store.witnesses);
  } catch (error) {
    // Echte Fehlerursache anzeigen (Konflikt, fehlender Zeitstempel, …) —
    // ein pauschaler Text verschleiert sonst, was schiefgelaufen ist.
    const msg = error instanceof Error ? error.message : (error as any)?.message || "Fehler beim Speichern. Bitte kontaktieren Sie den Support.";
    snackbar.showSnackbar({ message: msg, type: "error" });
    // Wie saveAccident/savePoliceProsecution weiterreichen, damit Aufrufer
    // (z. B. das Cockpit-Fenster) keinen Erfolg melden, wenn nichts gespeichert wurde.
    throw error;
  }
}

export async function addWitness(): Promise<void> {
  const store = useCaseStore();
  const response = await postRequestCase(`/protected/cases/${store.caseData.id}/witnesses`);
  store.witnesses.push(prepareWitnessForLoad(response));
  store.lastSavedWitnesses = cloneDeep(store.witnesses);
}

export async function deleteWitness(idWitness: string): Promise<void> {
  const store = useCaseStore();
  const index = store.witnesses.findIndex((v) => String(v.id) === String(idWitness));
  if (index === -1) return;

  // Kundenfeedback 26.10.2026 (VIII): Löschen aus dem Fragebogen – ein
  // Eintrag mit Inhalt bleibt in der Akte, bis die Kanzlei entscheidet.
  await deleteRequestCase(withQuestionnaireSource(`/protected/cases/${store.caseData.id}/witnesses/${idWitness}`, store.questionnaireView));
  store.witnesses.splice(index, 1);
  store.lastSavedWitnesses = cloneDeep(store.witnesses);
}

// data helper

type Witness = Record<string, any>;

export function prepareWitnessForLoad(data: Witness): Witness {
  return {
    ...data,

    witnessType: d.loadSelect(data.witnessType),
    roles: d.loadArray(data.roles),
    relation: d.loadSelect(data.relation),
    injured: d.loadTristate(data.injured),

    salutation: d.loadSelect(data.salutation),
    title: d.loadSelect(data.title),

    contactDataAvailable: d.loadTristate(data.contactDataAvailable),
    country: d.loadSelect(data.country),
  };
}

export function prepareWitnessForSave(data: Witness): Witness {
  return {
    ...data,

    witnessType: d.saveSelect(data.witnessType),
    roles: d.saveArray(data.roles),
    relation: d.saveSelect(data.relation),
    injured: d.saveTristate(data.injured),

    salutation: d.saveSelect(data.salutation),
    title: d.saveSelect(data.title),

    contactDataAvailable: d.saveTristate(data.contactDataAvailable),
    country: d.saveSelect(data.country),
  };
}
